import React from 'react';

import { Input } from 'antd';

export const CHOOSER_DEFAULTS = Object.freeze({
  chooserPages: [],
  chooserOptions: [],
});

export function objectRecord(value) {
  return value && typeof value === 'object' ? value : {};
}

export function firstTruthy(...values) {
  return values.find(Boolean);
}

export function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

export function resolvePageIndicator(composerChooser, normalizedChooserPage, chooserTotalPages) {
  const currentQuestion = objectRecord(objectRecord(composerChooser).currentQuestion);
  const hasQuestionProgress = [currentQuestion.stepIndex, currentQuestion.stepTotal].every(Boolean);
  return hasQuestionProgress
    ? `${currentQuestion.stepIndex} of ${currentQuestion.stepTotal}`
    : `${normalizedChooserPage} of ${chooserTotalPages}`;
}

export function nextChoiceKeys({ choiceKey, chooserSelectedKeys, chooserSelectionMode }) {
  const selectedKeys = Array.isArray(chooserSelectedKeys) ? chooserSelectedKeys : [];
  if (chooserSelectionMode !== 'multiple') {
    return [choiceKey];
  }
  return selectedKeys.includes(choiceKey)
    ? selectedKeys.filter((item) => item !== choiceKey)
    : [...selectedKeys, choiceKey];
}

export function applyChoiceSelection({ chooserSelectionMode, nextKeys, setChooserSelectedKey, setChooserSelectedKeys }) {
  if (chooserSelectionMode === 'multiple') {
    setChooserSelectedKeys?.(nextKeys);
    return;
  }
  setChooserSelectedKey(nextKeys[0] || '');
}

export function shouldSubmitChoice({ choice, chooserConfirmOnSelect, wasSelected, chooserSelectionMode }) {
  const allowsImmediateSubmit = objectRecord(choice).allowCustomInput !== true;
  return chooserSelectionMode !== 'multiple'
    && [chooserConfirmOnSelect, wasSelected].some(Boolean)
    && allowsImmediateSubmit;
}

export function handleChoiceSelection({
  choice,
  choiceKey,
  chooserSelectedKey,
  chooserConfirmOnSelect,
  setChooserSelectedKey,
  handleChooserSubmit,
  chooserSelectedKeys,
  chooserSelectionMode,
  setChooserSelectedKeys,
}) {
  const choiceRecord = objectRecord(choice);
  const wasSelected = chooserSelectedKey === choiceKey;
  const nextKeys = nextChoiceKeys({ choiceKey, chooserSelectedKeys, chooserSelectionMode });
  applyChoiceSelection({ chooserSelectionMode, nextKeys, setChooserSelectedKey, setChooserSelectedKeys });
  const shouldSubmit = shouldSubmitChoice({
    choice: choiceRecord, chooserConfirmOnSelect, wasSelected, chooserSelectionMode,
  });
  if (shouldSubmit) {
    handleChooserSubmit(choice);
  }
}

export function handleInlineInputKeyDown({
  event,
  chooserSubmitHotkey,
  handleChooserDismiss,
  handleChooserSubmit,
}) {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    handleChooserDismiss();
    return;
  }
  const submitHotkey = objectRecord(chooserSubmitHotkey);
  if (typeof submitHotkey.match === 'function' && submitHotkey.match(event) === true) {
    event.preventDefault();
    event.stopPropagation();
    handleChooserSubmit();
    return;
  }
  event.stopPropagation();
}

export function ComposerChooserOption({
  disabled,
  choice,
  index,
  chooserSelectedKey,
  chooserSelectedKeys,
  chooserSelectionMode,
  chooserRecommendedKey,
  chooserConfirmOnSelect,
  chooserInlineInputRef,
  chooserCustomInput,
  chooserSubmitHotkey,
  setChooserSelectedKey,
  setChooserSelectedKeys,
  setChooserCustomInput,
  handleChooserDismiss,
  handleChooserSubmit,
}) {
  const choiceRecord = objectRecord(choice);
  const choiceKey = String(choiceRecord.key ?? `choice-${index}`);
  const isSelected = chooserSelectionMode === 'multiple'
    ? Array.isArray(chooserSelectedKeys) && chooserSelectedKeys.includes(choiceKey)
    : chooserSelectedKey === choiceKey;
  const isRecommended = chooserRecommendedKey === choiceKey;
  const showInlineCustomInput = [isSelected, choiceRecord.allowCustomInput === true].every(Boolean);
  const description = String(choiceRecord.description ?? '').trim();
  const itemClassName = ['flare-composer-codex-chooser-item', isSelected ? 'is-selected' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      key={choiceKey}
      className={itemClassName}
      disabled={disabled}
      type="button"
      onClick={() => handleChoiceSelection({
        choice,
        choiceKey,
        chooserSelectedKey,
        chooserConfirmOnSelect,
        chooserSelectedKeys,
        chooserSelectionMode,
        setChooserSelectedKey,
        setChooserSelectedKeys,
        handleChooserSubmit,
      })}
    >
      <span className="flare-composer-codex-chooser-index">{index + 1}.</span>
      <span className="flare-composer-codex-chooser-content">
        {conditionalValue(showInlineCustomInput, (
          <Input
            disabled={disabled}
            className="flare-composer-codex-chooser-inline-input"
            placeholder="其他，请输入"
            ref={chooserInlineInputRef}
            value={chooserCustomInput}
            onClick={(event) => event.stopPropagation()}
            onFocus={() => setChooserSelectedKey(choiceKey)}
            onKeyDown={(event) => handleInlineInputKeyDown({
              event,
              chooserSubmitHotkey,
              handleChooserDismiss,
              handleChooserSubmit,
            })}
            onChange={(event) => setChooserCustomInput(event.target.value)}
          />
        ))}
        {conditionalValue(!showInlineCustomInput, (
          <span className="flare-composer-codex-chooser-text-wrap">
            <span className="flare-composer-codex-chooser-text">
              {choiceRecord.text ?? '选项'}
              {conditionalValue(isRecommended, ' (Recommended)', '')}
            </span>
            {conditionalValue(Boolean(description), (
              <span className="flare-composer-codex-chooser-description">{description}</span>
            ))}
          </span>
        ))}
      </span>
    </button>
  );
}

export function ComposerChooserOtherInput({
  disabled,
  chooserAllowsOtherInput,
  chooserOtherInputSelected,
  chooserOptions,
  chooserInlineInputRef,
  chooserCustomInput,
  chooserSubmitHotkey,
  setChooserOtherInputSelected,
  setChooserCustomInput,
  handleChooserDismiss,
  handleChooserSubmit,
}) {
  if (!chooserAllowsOtherInput) {
    return null;
  }
  const itemClassName = [
    'flare-composer-codex-chooser-item',
    chooserOtherInputSelected ? 'is-selected' : '',
  ].filter(Boolean).join(' ');
  return (
    <button
      className={itemClassName}
      disabled={disabled}
      type="button"
      aria-selected={chooserOtherInputSelected}
      onClick={() => setChooserOtherInputSelected(!chooserOtherInputSelected)}
    >
      <span className="flare-composer-codex-chooser-index">{chooserOptions.length + 1}.</span>
      <span className="flare-composer-codex-chooser-content">
        {conditionalValue(chooserOtherInputSelected, (
          <Input
            disabled={disabled}
            className="flare-composer-codex-chooser-inline-input"
            placeholder="其他，请输入"
            ref={chooserInlineInputRef}
            value={chooserCustomInput}
            onClick={(event) => event.stopPropagation()}
            onFocus={() => setChooserOtherInputSelected(true)}
            onKeyDown={(event) => handleInlineInputKeyDown({
              event,
              chooserSubmitHotkey,
              handleChooserDismiss,
              handleChooserSubmit,
            })}
            onChange={(event) => setChooserCustomInput(event.target.value)}
          />
        ))}
        {conditionalValue(!chooserOtherInputSelected, (
          <span className="flare-composer-codex-chooser-text-wrap">
            <span className="flare-composer-codex-chooser-text">其他</span>
          </span>
        ))}
      </span>
    </button>
  );
}
